import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type { KanbanBoardRequest } from '../utils/kanban-filter.utils';
import type { KanbanApiEntityType } from '../config/kanban-entity.config';
import type {
  KanbanSavedView,
  KanbanMoveResponse,
  KanbanBoardResponse,
  KanbanGroupByOption,
  KanbanColumnCardsResponse,
} from '../kanban-types';

const KANBAN_API = {
  board: (entityType: KanbanApiEntityType) => `api/kanban/${entityType}/board`,
  columnCards: (entityType: KanbanApiEntityType, columnKey: string) =>
    `api/kanban/${entityType}/column/${encodeURIComponent(columnKey)}/cards`,
  moveStage: (entityType: KanbanApiEntityType, id: string) =>
    `api/kanban/${entityType}/${id}/stage`,
  moveCard: (entityType: KanbanApiEntityType, id: string) => `api/kanban/${entityType}/${id}/move`,
  groupByOptions: (entityType: KanbanApiEntityType) => `api/kanban/${entityType}/group-by-options`,
  savedViews: 'api/kanban/saved-views',
};

type BoardArgs = {
  entityType: KanbanApiEntityType;
  body: KanbanBoardRequest;
};

type ColumnCardsArgs = BoardArgs & {
  columnKey: string;
};

type MoveStageArgs = {
  entityType: KanbanApiEntityType;
  id: string;
  stageId: string;
};

type MoveCardArgs = {
  entityType: KanbanApiEntityType;
  id: string;
  groupBy: string;
  targetColumnKey: string;
};

export const kanbanApi = createApi({
  reducerPath: 'kanbanApi',
  baseQuery,
  tagTypes: ['KanbanBoard', 'KanbanSavedViews'],
  endpoints: (builder) => ({
    getKanbanBoard: builder.query<KanbanBoardResponse, BoardArgs>({
      query: ({ entityType, body }) => ({
        url: KANBAN_API.board(entityType),
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data?: KanbanBoardResponse }) =>
        response?.data ?? ({} as KanbanBoardResponse),
      providesTags: (_result, _error, { entityType, body }) => [
        { type: 'KanbanBoard', id: `${entityType}-${body.groupBy}` },
      ],
    }),

    getKanbanColumnCards: builder.mutation<KanbanColumnCardsResponse, ColumnCardsArgs>({
      query: ({ entityType, columnKey, body }) => ({
        url: KANBAN_API.columnCards(entityType, columnKey),
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data?: KanbanColumnCardsResponse }) =>
        response?.data ?? ({} as KanbanColumnCardsResponse),
    }),

    moveKanbanStage: builder.mutation<KanbanMoveResponse, MoveStageArgs>({
      query: ({ entityType, id, stageId }) => ({
        url: KANBAN_API.moveStage(entityType, id),
        method: 'PATCH',
        body: { stageId },
      }),
      transformResponse: (response: { data?: KanbanMoveResponse }) =>
        response?.data ?? ({} as KanbanMoveResponse),
      invalidatesTags: ['KanbanBoard'],
    }),

    moveKanbanCard: builder.mutation<KanbanMoveResponse, MoveCardArgs>({
      query: ({ entityType, id, groupBy, targetColumnKey }) => ({
        url: KANBAN_API.moveCard(entityType, id),
        method: 'PATCH',
        body: { groupBy, targetColumnKey },
      }),
      transformResponse: (response: { data?: KanbanMoveResponse }) =>
        response?.data ?? ({} as KanbanMoveResponse),
      invalidatesTags: ['KanbanBoard'],
    }),

    getKanbanGroupByOptions: builder.query<KanbanGroupByOption[], KanbanApiEntityType>({
      query: (entityType) => ({
        url: KANBAN_API.groupByOptions(entityType),
        method: 'GET',
      }),
      transformResponse: (response: { data?: { options?: KanbanGroupByOption[] } }) =>
        response?.data?.options ?? [],
    }),

    getKanbanSavedViews: builder.query<KanbanSavedView[], string>({
      query: (entityType) => ({
        url: `${KANBAN_API.savedViews}?entityType=${entityType}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        data?: KanbanSavedView[] | { list?: KanbanSavedView[] };
      }) => {
        const data = response?.data;
        if (Array.isArray(data)) return data;
        return data?.list ?? [];
      },
      providesTags: ['KanbanSavedViews'],
    }),

    createKanbanSavedView: builder.mutation<KanbanSavedView, Partial<KanbanSavedView>>({
      query: (body) => ({
        url: KANBAN_API.savedViews,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data?: KanbanSavedView }) =>
        response?.data ?? ({} as KanbanSavedView),
      invalidatesTags: ['KanbanSavedViews'],
    }),

    updateKanbanSavedView: builder.mutation<
      KanbanSavedView,
      { id: string; body: Partial<KanbanSavedView> }
    >({
      query: ({ id, body }) => ({
        url: `${KANBAN_API.savedViews}/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: { data?: KanbanSavedView }) =>
        response?.data ?? ({} as KanbanSavedView),
      invalidatesTags: ['KanbanSavedViews'],
    }),

    deleteKanbanSavedView: builder.mutation<void, string>({
      query: (id) => ({
        url: `${KANBAN_API.savedViews}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['KanbanSavedViews'],
    }),
  }),
});

export const {
  useGetKanbanBoardQuery,
  useLazyGetKanbanBoardQuery,
  useGetKanbanColumnCardsMutation,
  useMoveKanbanStageMutation,
  useMoveKanbanCardMutation,
  useGetKanbanGroupByOptionsQuery,
  useGetKanbanSavedViewsQuery,
  useCreateKanbanSavedViewMutation,
  useUpdateKanbanSavedViewMutation,
  useDeleteKanbanSavedViewMutation,
} = kanbanApi;
